import Component from './component';
import { interpolate } from '../../../utils/math';
import debounce from '../../../utils/debounce';

export default class Waveform extends Component {
  constructor (props) {
    super(props);

    this.resize = debounce(this.resize.bind(this), 100);
    this.dataArray = [];
    this.bounds = { width: 0, height: 0 };
  }

  getDefaultProps () {
    return {
      lineWidth: 2,
      barGapSize: 1,
      color: 'rgb(50 50 50)',
      type: 'bars',
      mirrored: true,
      data: [],
      class: ''
    };
  }

  getMaxBars () {
    if (!this.isAttached()) return 0;

    if (this.bounds.width <= 0) {
      return 0;
    }

    return Math.floor((this.bounds.width - this.props.barGapSize) / (this.props.lineWidth + this.props.barGapSize));
  }

  attach (element) {
    super.attach(element);

    this.canvasContext = this.$element.getContext('2d');
    this.canvasContext.imageSmoothingEnabled = false;
  }

  render () {
    return `<canvas class="iono-waveform ${this.props.class}"></canvas>`;
  }

  update (changed = []) {
    if (changed.includes('data')) {
      this._resample();
      this._draw();
    }
  }

  resize (width = null, height = null) {
    if (!this.isAttached()) return;

    const bounds = { width, height };
    if (bounds.width === null || bounds.height === null) {
      const rect = this.$element.getClientRects()[0];
      bounds.width = rect.width;
      bounds.height = rect.height;
    }

    bounds.width = Math.round(bounds.width);
    bounds.height = Math.round(bounds.height);
    this.bounds = bounds;

    const pixelRatio = Math.ceil(window.devicePixelRatio);
    const dpiWidth = Math.round(bounds.width * pixelRatio);
    const dpiHeight = Math.round(bounds.height * pixelRatio);

    Object.assign(this.$element.style, {
      width: `${bounds.width}px`,
      height: `${bounds.height}px`
    });

    this.$element.width = dpiWidth;
    this.$element.height = dpiHeight;

    this.canvasContext.scale(pixelRatio, pixelRatio);

    this._resample();
    this._draw();
  }

  _resample () {
    const numBars = this.getMaxBars();
    const data = this.props.data.length ? this.props.data : Array(numBars).fill(0, 0);
    this.dataArray = interpolate(data, numBars);
  }

  _draw () {
    if (!this.isAttached()) return;

    this.canvasContext.clearRect(0, 0, this.bounds.width, this.bounds.height);

    switch (this.props.type) {
      case 'oscillator': return this._drawOscillator();
      default: return this._drawBars();
    }
  }

  _drawOscillator () {
    const dataLen = this.props.data.length;
    const sliceWidth = (this.$element.width * 1.0) / (dataLen - 1);

    let x = 0;

    this.canvasContext.lineWidth = this.props.lineWidth;
    this.canvasContext.strokeStyle = this.props.color;

    this.canvasContext.beginPath();
    for (let i = 0; i < dataLen; i++) {
      const v = this.props.data[i];
      const y = (v === 0.0 ? this.$element.height / 2 : (v * this.$element.height)) + (this.props.lineWidth / 2);

      if (i === 0) {
        this.canvasContext.moveTo(x, y);
      } else {
        this.canvasContext.lineTo(x, y);
      }

      x += sliceWidth;
    }
    this.canvasContext.stroke();
  }

  _drawBars () {
    const dataLen = this.dataArray.length;

    let barHeight;
    let x = 0;
    let y = 0;

    this.canvasContext.beginPath();

    this.canvasContext.lineWidth = this.props.lineWidth;
    this.canvasContext.lineCap = 'round';
    this.canvasContext.lineJoin = 'round';
    this.canvasContext.strokeStyle = this.props.color;
    this.canvasContext.fillStyle = this.props.color;

    for (let i = 0; i < dataLen; i++) {
      barHeight = Math.max(1, Math.round(this.dataArray[i] * (this.bounds.height - this.props.lineWidth * 2)));

      if ('roundRect' in this.canvasContext) {
        y = Math.round((this.bounds.height - barHeight) / 2);

        this.canvasContext.roundRect(x, y, this.props.lineWidth, barHeight, 45);
      } else {
        y = Math.round((this.bounds.height + barHeight) / 2);

        this.canvasContext.moveTo(x, y);
        this.canvasContext.lineTo(x, y - barHeight);
      }

      // console.log(`[${x}, ${y}, ${x}, ${y - barHeight}]`);

      x += this.props.lineWidth + this.props.barGapSize;
    }

    if ('roundRect' in this.canvasContext) {
      this.canvasContext.fill();
    } else {
      this.canvasContext.stroke();
    }
  }
}
